import { faPencil } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid, Box, Dialog, TextField } from "@mui/material";
import { isEqual } from "lodash";
import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { Props, State } from "./interfaces";
import { Button, IconButton, DialogContent } from "./styles";
import ConfirmationChangesDialog from "../../../../../containers/ConfirmationChangesDialog/ConfirmationChangesDialog";
import DialogActions from "../../../../../containers/DialogActions/DialogActions";
import DialogTitle from "../../../../../containers/DialogTitle/DialogTitle";
import LoadingOverlay from "../../../../../containers/LoadingOverlay/LoadingOverlay";
import { useAppSelector } from "../../../../../custom-hooks/redux/redux";
import { LegalEntitySISTO } from "../../../../../models/legal-entity-si";
import MaskedInput from "../../../../../reusable-components/MaskedInput/MaskedInput";

import { saveLegalEntitySIs } from "../../../../../services/legal-entity-sis/legal-entity-sis-api.service";

const AddEditPassword = ({ effect, passwordItem, handleNotifyParent, legalEntity }: Props) => {
  const { t } = useTranslation();
  const tRP = "pages.document_reports.compliance_documents.passwords";

  const { currentCompanyAccount } = useAppSelector((state) => state.currentUserReducer);

  const EMPTY_PASSWORD_ITEM: LegalEntitySISTO = useMemo(
    () => ({
      id: "",
      isDeleted: false,
      companyAccount: currentCompanyAccount,
      legalEntity: null,
      description: "",
      domain: "",
      user: "",
      password: "",
      documents: [],
    }),
    [currentCompanyAccount]
  );

  const [state, setState] = useState<State>({
    openDialog: false,
    openConfirmationChangesDialog: false,
    loading: false,
    error: "",
    password: passwordItem ?? EMPTY_PASSWORD_ITEM,
  });

  const setOpenDialog = (dialog: "openDialog" | "openConfirmationChangesDialog", value: boolean) =>
    setState((state) => ({ ...state, [dialog]: value }));

  const handleCloseDialog = () => {
    if (state.loading) return;

    isEqual(effect === "add" ? EMPTY_PASSWORD_ITEM : passwordItem, state.password)
      ? setOpenDialog("openDialog", false)
      : setOpenDialog("openConfirmationChangesDialog", true);
  };

  const handleResetState = () =>
    setState((state) => ({
      ...state,
      openDialog: false,
      openConfirmationChangesDialog: false,
      error: "",
      password: passwordItem ?? EMPTY_PASSWORD_ITEM,
    }));

  const handleChangePasswordItem = ({ target }: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const { name, value } = target;

    setState((state) => ({
      ...state,
      password: { ...state.password, [name]: value },
    }));
  };

  const handleSavePasswordItem = async (password: LegalEntitySISTO | null) => {
    if (!password) return;

    setState((state) => ({ ...state, loading: true }));

    try {
      const { error } = await saveLegalEntitySIs({
        ...password,
        ...(effect === "add" && { id: uuidv4(), legalEntity }),
      });
      await handleNotifyParent(); // refetch data in parent component

      if (!error) handleResetState();
    } catch (error) {
      setState((state) => ({ ...state, error: error as string }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  };

  return (
    <Grid>
      {effect === "add" && (
        <Button
          variant="contained"
          onClick={() => setOpenDialog("openDialog", true)}
        >
          {`${t(`${tRP}.add_new_password`)}`}
        </Button>
      )}
      {effect === "edit" && (
        <IconButton
          color="primary"
          onClick={() => setOpenDialog("openDialog", true)}
        >
          <FontAwesomeIcon icon={faPencil} />
        </IconButton>
      )}
      <Dialog
        open={state.openDialog}
        onClose={handleCloseDialog}
        fullWidth
      >
        <LoadingOverlay loading={state.loading}>
          <DialogTitle
            title={t(`${tRP}.add_new_password`)}
            handleClose={handleCloseDialog}
          />
          <DialogContent>
            <Box
              component="form"
              autoComplete="off"
            >
              <TextField
                label={`${t(`${tRP}.table.fields.description`)}`}
                name="description"
                value={state.password?.description}
                onChange={handleChangePasswordItem}
              />
              <TextField
                label={`${t(`${tRP}.table.fields.domain`)}`}
                name="domain"
                value={state.password?.domain}
                onChange={handleChangePasswordItem}
              />
              <TextField
                autoComplete="new-username"
                label={`${t(`${tRP}.table.fields.user`)}`}
                name="user"
                value={state.password?.user}
                onChange={handleChangePasswordItem}
              />
              <MaskedInput
                label={`${t(`${tRP}.table.fields.password`)}`}
                name="password"
                value={state.password?.password ?? ""}
                handleChange={handleChangePasswordItem}
              />
            </Box>
          </DialogContent>
          <DialogActions
            handleCancel={handleCloseDialog}
            handleSave={() => handleSavePasswordItem(state.password)}
            error={state.error}
          />
        </LoadingOverlay>
      </Dialog>
      <ConfirmationChangesDialog
        open={state.openConfirmationChangesDialog}
        handleCancel={() => setOpenDialog("openConfirmationChangesDialog", false)}
        handleDiscard={handleResetState}
      />
    </Grid>
  );
};

export default AddEditPassword;
